import { jsx } from "react/jsx-runtime";
import { createContext, useRef } from "react";
import { LANGUAGES } from "@crehana/ts-types";
import { toJsx } from "./formatters.js";
import { CrehanaI18n } from "./i18n.js";
import { pipe, validateIsConfig, validateIsFormatterConfig } from "./utils.js";
const noopTFunction = (k, doc, conf)=>{
    if ('string' == typeof doc) return doc;
    if (validateIsConfig(doc) && doc.defaultValue) return doc.defaultValue;
    if (validateIsConfig(conf) && conf.defaultValue) return conf.defaultValue;
    return k;
};
const CrehanaI18nContext = /*#__PURE__*/ createContext({
    t: noopTFunction,
    addResourceBundle: ()=>true,
    languageCode: LANGUAGES.ES
});
function getFormatters(config) {
    const formatters = config.formatter ? [
        config.formatter
    ] : [];
    if (config.formatters?.length) config.formatters.forEach((f)=>{
        if (f) formatters.push(f);
    });
    return pipe(...formatters);
}
const KNOWN_KEYS = {
    defaultValue: true,
    namespace: true,
    interpolation: true,
    count: true,
    formatter: true,
    formatters: true
};
function parseExtraConfig(config) {
    const actualConfig = {
        ...config
    };
    const keys = Object.keys(config).filter((k)=>!KNOWN_KEYS[k]);
    if (keys.length) {
        actualConfig.interpolation = actualConfig.interpolation ?? {};
        keys.forEach((k)=>{
            if ('development' === process.env.NODE_ENV) {
                if (config[k]) console.warn('%c WARNING:\n', 'font-size: 18px', 'crehana-i18n no longer supports unknown keys as interpolation values', 'please update use the interpolation property in the config object', 'this was previously available as part of the migration from react-i18next.', `Found key: ${k}`);
            }
            actualConfig.interpolation[k] = config[k];
        });
    }
    return actualConfig;
}
const I18nProvider = ({ languageCode, countryPrefix, countryCode, translations, defaultTranslations, defaultNs, fallbackLanguageCode, fallbackNs, children })=>{
    const i18nInstance = useRef(new CrehanaI18n({
        language: languageCode,
        namespace: defaultNs
    }));
    const t = (key, defaultValueOrConfig, config)=>{
        if ('string' == typeof defaultValueOrConfig) {
            const conf = validateIsConfig(config) ? parseExtraConfig(config) : void 0;
            return i18nInstance.current.t(key, {
                defaultValue: defaultValueOrConfig,
                ...conf
            });
        }
        let result = '';
        result = validateIsConfig(defaultValueOrConfig) ? i18nInstance.current.t(key, parseExtraConfig(defaultValueOrConfig)) : i18nInstance.current.t(key);
        if (validateIsFormatterConfig(defaultValueOrConfig)) {
            const formatter = getFormatters(defaultValueOrConfig);
            return toJsx(formatter(result));
        }
        return result;
    };
    const addResourceBundle = (bundle)=>i18nInstance.current.addResourceBundle(bundle);
    i18nInstance.current.setLanguage(languageCode);
    if (defaultTranslations) addResourceBundle(defaultTranslations);
    if (translations) addResourceBundle(translations);
    if (defaultNs) i18nInstance.current.setNamespace(defaultNs);
    if (fallbackLanguageCode) i18nInstance.current.setFallbackLanguages([
        fallbackLanguageCode
    ]);
    if (fallbackNs) i18nInstance.current.setFallbackNamespaces(fallbackNs);
    return /*#__PURE__*/ jsx(CrehanaI18nContext.Provider, {
        value: {
            t,
            addResourceBundle,
            languageCode,
            countryCode: countryPrefix || countryCode
        },
        children: children
    });
};
export { CrehanaI18nContext, I18nProvider };
